// ==========================================================================
// Переписанный плагин рекламы специально для Anidub
// ==========================================================================
// @ts-check

import * as AdSdk from '@mailonline/video-ad-sdk/dist/main.esm';
import { createElement } from '../utils/elements';
import { triggerEvent } from '../utils/events';
import { formatTime } from '../utils/time';
import i18n from '../utils/i18n';
import is from '../utils/is';

import Plyr from '../plyr';

class Ads {
    /**
     *
     * @param {Plyr} player
     */
    constructor(player) {
        this.player = player;
        this.active = {};
        this.config = player.config.ads;
        this.events = {};
        this.responses = {};

        this.elements = {
            container: null,
        };

        this.initialized = false;
        this.playing = false;
        this.loaded = false;

        this.countdownTimer = null;
        this.managerPromise = new Promise((res, rej) => {
            this.on('loaded', res);
            this.on('error', rej);
        });

        if (this.enabled) {
            this.load();
        }
    }

    get enabled() {
        return (
            this.player.isHTML5 && this.player.isVideo &&
            this.player.config.ads.enabled && !is.empty(this.config) &&
            is.array(this.config.sources)
        );
    }

    /**
     * Готовит рекламный модуль
     */
    load() {
        if (this.enabled) {
            this.spawnContainer();

            this.config.sources = this.config.sources
                .filter(({ enabled }) => enabled);

            this.status = 'инициализация рекламы';
            this.trigger('loaded');
        }
    }

    /**
     * Создаём контейнеры
     */
    spawnContainer(force = false) {
        if (this.enabled) {
            if (force || !is.element(this.elements.container)) {
                if (is.element(this.elements.container)) {
                    this.elements.container.remove();
                }

                this.elements.container = createElement('div', {
                    class: this.player.config.classNames.ads,
                });

                this.player.elements.container.appendChild(this.elements.container);
            }
        }
    }

    /**
     *
     */
    requestAds() {
        if (this.enabled) {
            this.status = 'инициализация рекламы';
            // делаем запросы ко всем
            const requests = this.config.sources
                .map(async (src, i) => {
                    try {
                        const response = {
                            chains: await AdSdk.requestAd(src.url, {
                                AllowMultipleAds: false,
                                timeout: this.config.timeout,
                            }),
                            used: false,
                            unit: {},
                            index: i - 1,
                            ...src,
                        };

                        return response;
                    } catch (e) {
                        this.player.debug.warn(`AD: source get for ${src.url} error:`, e);

                        return false;
                    }
                });

            Promise.all(requests)
                .then((responses) => {
                    this.responses = responses
                        .filter(res => is.object(res)) // отсеиваем ошибки при запросе
                        .filter(res => !res.chains[0].errorCode) // отсеиваем ошибки рекламы
                        .sort(({ index: x }, { index: i }) => x - i) // сортируем по индексу
                        .map((x, i) => ({
                            timeout: this.config.timeout,
                            ...x,
                            index: i,
                        })); // исправляем индексы согласно ключам и добавляем таймаут если его нет

                    if (is.empty(this.responses)) {
                        this.initialized = true;
                        if (is.empty(responses.filter(x => x === true))) {
                            this.onAdError(new Error('No ads, everything blocked'), false);
                            triggerEvent.call(this.player, this.player.media, 'adsblocked');
                        } else {
                            this.onAdError(new Error(`AD: Responses chain is empty, all ads have errors: ${responses}`));
                        }

                        return;
                    }

                    // получает неиспользованную рекламу
                    this.rotateNextAd();

                    triggerEvent.call(this.player, this.player.media, 'adsloaded');

                    // ad is ready
                    this.loaded = true;

                    // play
                    this.play();
                })
                .catch(() => {
                    this.trigger('error');
                });
        }
    }

    /**
     * Получает следующую неиспользованную рекламу
     */
    rotateNextAd() {
        this.status = 'поиск рекламы';

        // получаем неиспользованные источники
        const unused = this.responses
            .filter(({ used }) => !used);

        // если неиспользованных источников не осталось
        if (is.empty(unused)) {
            return false;
        }

        // eslint-disable-next-line prefer-destructuring
        this.active = unused[0];

        // отмечаем как использованную
        this.responses[unused[0].index].used = true;

        // возвращаем
        return this.active;
    }

    play() {
        if (!this.loaded) {
            this.pauseContent();
            this.requestAds();
            return;
        }

        const { container } = this.elements;

        if (!this.managerPromise) {
            this.resumeContent();
        }

        this.managerPromise
            .then(() => {
                if (!this.initialized) {
                    AdSdk.run(this.active.chains, container, {
                        timeout: this.active.timeout,
                        responsive: true,
                        onAdReady: (unit) => {
                            this.status = 'загрузка рекламы';

                            this.active.unit = unit;

                            // fire content loaded event
                            triggerEvent.call(this.player, this.player.media, 'adscontentloaded');
                        },
                    })
                        .then((unit) => {
                            this.initialized = true;
                            this.status = 'показ рекламы';
                            unit.setVolume(this.player.volume);
                            unit.on('finish', () => {
                                this.cancel();
                                this.pollCountdown();

                                triggerEvent.call(this.player, this.player.media, 'adscomplete');
                            });

                            this.pollCountdown(true);

                            // Fire event
                            triggerEvent.call(this.player, this.player.media, 'adstarted');
                        })
                        .catch((e) => {
                            this.player.debug.warn('AD: error, trying to get next ad', e);
                            this.status = 'поиск другой рекламы';
                            if (!this.rotateNextAd()) {
                                this.player.debug.warn('AD: There is no unused ads now, destroying.');

                                this.initialized = true;
                                this.onAdError(e);

                                return;
                            }

                            this.player.debug.warn('AD: playing next ad');
                            this.play();
                        });
                }
            })
            .catch(() => {});
    }

    /**
     * Update the ad countdown
     * @param {boolean} start
     */
    pollCountdown(start = false) {
        if (!start) {
            clearInterval(this.countdownTimer);
            this.elements.container.removeAttribute('data-badge-text');
            this.elements.container.removeAttribute('data-badge-hover-text');
            return;
        }

        const update = () => {
            const remainingTime = this.active.unit.duration() - this.active.unit.currentTime();
            const time = remainingTime >= 45
                ? 'подождите'
                : formatTime(Math.max(remainingTime, 0));

            const label = `${i18n.get('advertisement', this.player.config)} - ${time}`;
            const labelHover = `${i18n.get('adexplanation', this.player.config)} - ${time}`;

            this.elements.container.setAttribute('data-badge-text', label);
            this.elements.container.setAttribute('data-badge-hover-text', labelHover);
        };

        this.countdownTimer = setInterval(update, 100);
    }

    get status() {
        return this.elements.container.getAttribute('data-loading-text');
    }

    set status(value) {
        this.elements.container.setAttribute('data-loading-text', value.toLowerCase());
    }

    /**
     * Resume our video
     */
    resumeContent() {
        this.status = 'приятного просмотра';

        // Hide the advertisement container
        this.elements.container.style.zIndex = '';

        // Ad is stopped
        this.playing = false;

        // Play our video
        if (this.player.currentTime < this.player.duration) {
            this.player.play();
        }
    }

    /**
     * Pause our video
     */
    pauseContent() {
        // Show the advertisement container
        this.elements.container.style.zIndex = 3;

        // Ad is playing.
        this.playing = true;

        // Pause our video.
        this.player.pause();
    }

    /**
     *
     * @param {string} event
     * @param  {...any} args
     */
    trigger(event, ...args) {
        const handlers = this.events[event];

        if (is.array(handlers)) {
            handlers.forEach(handler => {
                if (is.function(handler)) {
                    handler.apply(this, args);
                }
            });
        }
    }

    /**
     *
     * @param {string} event Тип события
     * @param {function} callback Действие при срабатывания события
     */
    on(event, callback) {
        if (!is.array(this.events[event])) {
            this.events[event] = [];
        }

        this.events[event].push(callback);
        return this;
    }

    onAdError(e, fire = true) {
        this.player.debug.error('Ads error:', e);

        if (fire) {
            // Fire event
            triggerEvent.call(this.player, this.player.media, 'adserror');
        }

        this.cancel();
    }

    cancel() {
        if (this.initialized &&
            Object.prototype.hasOwnProperty.call(this.active, 'unit') &&
            Object.prototype.hasOwnProperty.call(this.active.unit, 'cancel')
        ) {
            this.player.debug.log('Ad is initialized, destroying...');
            this.active.unit.cancel()
                .catch(() => {});
        }

        this.resumeContent();

        this.player.debug.log('Ad is canceled');
        this.trigger('error');
    }
}

export default Ads;
